import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { createAnalyticsHelpers } from '../../lzapps_core_code/business/analytics';
import { GET_APP_NAME, BUNDLE_APP_NAME } from '../../app_constant';
import { AuthContext } from './AuthProvider';
import { BundleContext } from './BundleProvider';

export const AnalyticsContext = createContext();

export const AnalyticsProvider = ({ children }) => {
  const posthog = usePostHog();
  const { isSubscribe } = useContext(AuthContext) || {};
  const { selectedCertificate } = useContext(BundleContext) || {};
  const helpersRef = useRef(null);

  // Create helpers once using the PostHog client
  if (!helpersRef.current && posthog) {
    helpersRef.current = createAnalyticsHelpers(posthog);
  }

  // Register super properties after PostHog is fully initialized
  useEffect(() => {
    if (!posthog) return;
    const selectedCertificate = localStorage.getItem('selectedCertificate') || 'cisa';
    posthog.register({
      app_name: BUNDLE_APP_NAME,
      exam_name: GET_APP_NAME(selectedCertificate),
      platform: 'web',
      source_environment: process.env.NODE_ENV || 'production',
    });
  }, [posthog]);

  // Sync exam name when certificate changes
  useEffect(() => {
    if (!posthog || !selectedCertificate) return;
    posthog.register({ exam_name: GET_APP_NAME(selectedCertificate) });
  }, [posthog, selectedCertificate]);

  // Sync subscription status to PostHog as super + person property
  useEffect(() => {
    if (!posthog || isSubscribe === undefined) return;
    posthog.register({ is_premium: !!isSubscribe });
    posthog.setPersonProperties({ is_premium: !!isSubscribe });
  }, [posthog, isSubscribe]);

  // Fallback capture for when helpers aren't ready yet
  const capture = useCallback((eventName, properties = {}) => {
    if (posthog) {
      posthog.capture(eventName, properties);
    }
  }, [posthog]);

  // Core tracking
  const track = useCallback((eventName, properties = {}) => {
    capture(eventName, properties);
  }, [capture]);

  const trackEvent = track; // alias

  // Identify user (call on login)
  const identify = useCallback((userId, traits = {}) => {
    if (posthog) {
      posthog.identify(userId, traits);
    }
  }, [posthog]);

  // Update super properties when user or exam changes
  const updateUserInfo = useCallback((user) => {
    if (posthog && user?.uid) {
      posthog.identify(user.uid, {
        email: user.email,
        app_name: BUNDLE_APP_NAME,
      });
    }
  }, [posthog]);

  const updateExamName = useCallback((selectedCertificate) => {
    if (posthog) {
      posthog.register({
        exam_name: GET_APP_NAME(selectedCertificate),
      });
    }
  }, [posthog]);

  const updateSubscriptionStatus = useCallback((isPremium) => {
    if (posthog) {
      // Super property — attached to every event
      posthog.register({ is_premium: isPremium });
      // Person property — visible in PostHog People profiles
      posthog.setPersonProperties({ is_premium: isPremium });
    }
  }, [posthog]);

  const resetUser = useCallback(() => {
    if (posthog) {
      posthog.reset();
    }
  }, [posthog]);

  // All typed helpers wrapped in useCallback for stable references
  const trackSignup = useCallback((method, properties = {}) => {
    helpersRef.current?.trackSignup(method, properties);
  }, []);

  const trackLogin = useCallback((method, properties = {}) => {
    helpersRef.current?.trackLogin(method, properties);
  }, []);

  const trackLogout = useCallback((properties = {}) => {
    helpersRef.current?.trackLogout(properties);
  }, []);

  const trackPasswordReset = useCallback((properties = {}) => {
    helpersRef.current?.trackPasswordReset(properties);
  }, []);

  const trackOnboardingStep = useCallback((step, properties = {}) => {
    helpersRef.current?.trackOnboardingStep(step, properties);
  }, []);

  const trackExamSelection = useCallback((exam, properties = {}) => {
    helpersRef.current?.trackExamSelection(exam, properties);
  }, []);

  const trackAssessmentStart = useCallback((properties = {}) => {
    helpersRef.current?.trackAssessmentStart(properties);
  }, []);

  const trackAssessmentComplete = useCallback((score, properties = {}) => {
    helpersRef.current?.trackAssessmentComplete(score, properties);
  }, []);

  const trackOnboardingComplete = useCallback((properties = {}) => {
    helpersRef.current?.trackOnboardingComplete(properties);
  }, []);

  const trackStudySessionStart = useCallback((topic, properties = {}) => {
    helpersRef.current?.trackStudySessionStart(topic, properties);
  }, []);

  const trackStudySessionComplete = useCallback((topic, duration, questionsAnswered, properties = {}) => {
    helpersRef.current?.trackStudySessionComplete(topic, duration, questionsAnswered, properties);
  }, []);

  const trackQuestionAnswer = useCallback((questionId, isCorrect, topic, properties = {}) => {
    helpersRef.current?.trackQuestionAnswer(questionId, isCorrect, topic, properties);
  }, []);

  const trackQuestionBookmark = useCallback((questionId, action, properties = {}) => {
    helpersRef.current?.trackQuestionBookmark(questionId, action, properties);
  }, []);

  const trackTestStart = useCallback((testType, testId, properties = {}) => {
    helpersRef.current?.trackTestStart(testType, testId, properties);
  }, []);

  const trackTestComplete = useCallback((testType, testId, score, duration, properties = {}) => {
    helpersRef.current?.trackTestComplete(testType, testId, score, duration, properties);
  }, []);

  const trackTestAbandon = useCallback((testType, testId, reason, properties = {}) => {
    helpersRef.current?.trackTestAbandon(testType, testId, reason, properties);
  }, []);

  const trackFlashcardView = useCallback((cardId, properties = {}) => {
    helpersRef.current?.trackFlashcardView(cardId, properties);
  }, []);

  const trackFlashcardAnswer = useCallback((cardId, isCorrect, properties = {}) => {
    helpersRef.current?.trackFlashcardAnswer(cardId, isCorrect, properties);
  }, []);

  const trackSubscriptionView = useCallback((plan, properties = {}) => {
    helpersRef.current?.trackSubscriptionView(plan, properties);
  }, []);

  const trackSubscriptionStart = useCallback((plan, properties = {}) => {
    helpersRef.current?.trackSubscriptionStart(plan, properties);
  }, []);

  const trackSubscriptionCancel = useCallback((plan, properties = {}) => {
    helpersRef.current?.trackSubscriptionCancel(plan, properties);
  }, []);

  const trackPurchaseIntent = useCallback((planName, properties = {}) => {
    helpersRef.current?.trackPurchaseIntent(planName, properties);
  }, []);

  const trackPurchaseComplete = useCallback((planName, amount, properties = {}) => {
    helpersRef.current?.trackPurchaseComplete(planName, amount, properties);
  }, []);

  /** @deprecated Use track(eventName, props) or a specific typed helper instead. */
  const trackFeatureUsage = useCallback((feature, properties = {}) => {
    helpersRef.current?.trackFeatureUsage(feature, properties);
  }, []);

  const trackCustomTestCreate = useCallback((domainCount, questionCount, properties = {}) => {
    helpersRef.current?.trackCustomTestCreate(domainCount, questionCount, properties);
  }, []);

  const trackProfileUpdate = useCallback((field, properties = {}) => {
    helpersRef.current?.trackProfileUpdate(field, properties);
  }, []);

  const trackSearch = useCallback((query, resultsCount, properties = {}) => {
    helpersRef.current?.trackSearch(query, resultsCount, properties);
  }, []);

  const trackShare = useCallback((content, platform, properties = {}) => {
    helpersRef.current?.trackShare(content, platform, properties);
  }, []);

  const trackArticleView = useCallback((articleId, properties = {}) => {
    helpersRef.current?.trackArticleView(articleId, properties);
  }, []);

  const trackArticleBookmark = useCallback((articleId, action, properties = {}) => {
    helpersRef.current?.trackArticleBookmark(articleId, action, properties);
  }, []);

  const trackArticleReaction = useCallback((articleId, action, properties = {}) => {
    helpersRef.current?.trackArticleReaction(articleId, action, properties);
  }, []);

  // QOTD
  const trackQOTDView = useCallback((properties = {}) => {
    helpersRef.current?.trackQOTDView(properties);
  }, []);

  const trackQOTDAnswer = useCallback((isCorrect, properties = {}) => {
    helpersRef.current?.trackQOTDAnswer(isCorrect, properties);
  }, []);

  const trackAppError = useCallback((errorType, errorMessage, properties = {}) => {
    helpersRef.current?.trackAppError(errorType, errorMessage, properties);
  }, []);

  const trackPageLoadTime = useCallback((loadTime, properties = {}) => {
    helpersRef.current?.trackPageLoadTime(loadTime, properties);
  }, []);

  const trackEngagement = useCallback((action, duration, properties = {}) => {
    helpersRef.current?.trackEngagement(action, duration, properties);
  }, []);

  const trackUserAction = useCallback((action, properties = {}) => {
    helpersRef.current?.trackUserAction(action, properties);
  }, []);

  const trackPageView = useCallback((pagePath, properties = {}) => {
    helpersRef.current?.trackPageView(pagePath, properties);
  }, []);

  const trackNavigation = useCallback((from, to, properties = {}) => {
    helpersRef.current?.trackNavigation(from, to, properties);
  }, []);

  const value = useMemo(() => ({
    // Core
    track,
    trackEvent,
    identify,
    resetUser,

    // Authentication
    trackSignup,
    trackLogin,
    trackLogout,
    trackPasswordReset,

    // Onboarding
    trackOnboardingStep,
    trackExamSelection,
    trackAssessmentStart,
    trackAssessmentComplete,
    trackOnboardingComplete,

    // Study
    trackStudySessionStart,
    trackStudySessionComplete,
    trackQuestionAnswer,
    trackQuestionBookmark,

    // Tests
    trackTestStart,
    trackTestComplete,
    trackTestAbandon,

    // Flashcards
    trackFlashcardView,
    trackFlashcardAnswer,

    // Subscription & Purchase
    trackSubscriptionView,
    trackSubscriptionStart,
    trackSubscriptionCancel,
    trackPurchaseIntent,
    trackPurchaseComplete,

    // Features
    trackFeatureUsage,
    trackCustomTestCreate,
    trackProfileUpdate,
    trackSearch,
    trackShare,

    // Articles
    trackArticleView,
    trackArticleBookmark,
    trackArticleReaction,

    // QOTD
    trackQOTDView,
    trackQOTDAnswer,

    // Errors & Performance
    trackAppError,
    trackPageLoadTime,
    trackEngagement,
    trackUserAction,
    trackPageView,
    trackNavigation,

    // Utility
    updateUserInfo,
    updateExamName,
    updateSubscriptionStatus,
  }), [
    track, trackEvent, identify, resetUser,
    trackSignup, trackLogin, trackLogout, trackPasswordReset,
    trackOnboardingStep, trackExamSelection, trackAssessmentStart, trackAssessmentComplete, trackOnboardingComplete,
    trackStudySessionStart, trackStudySessionComplete, trackQuestionAnswer, trackQuestionBookmark,
    trackTestStart, trackTestComplete, trackTestAbandon,
    trackFlashcardView, trackFlashcardAnswer,
    trackSubscriptionView, trackSubscriptionStart, trackSubscriptionCancel, trackPurchaseIntent, trackPurchaseComplete,
    trackFeatureUsage, trackCustomTestCreate, trackProfileUpdate, trackSearch, trackShare,
    trackArticleView, trackArticleBookmark, trackArticleReaction,
    trackQOTDView, trackQOTDAnswer,
    trackAppError, trackPageLoadTime, trackEngagement, trackUserAction, trackPageView, trackNavigation,
    updateUserInfo, updateExamName, updateSubscriptionStatus,
  ]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom hook to use analytics
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
